import { orderBy } from "es-toolkit";
import { makeAutoObservable, reaction, runInAction } from "mobx";

import { userTeams, disposeApi } from "@/api";
import { TeamModel } from "@/models/TeamModel";

import type { TeamShort } from "@/api";
import type { RootStore } from "@/store/RootStore";
import type { LazyStore } from "@/store/types";

export const CURRENT_TEAM_STORAGE_KEY = "current-team";

export class TeamsStore implements LazyStore {
  currentTeam?: TeamModel = undefined;

  userTeams: TeamModel[] = [];

  fetchingProcess: boolean = false;

  private loadTeamsDispose?: () => void;

  private userDataReactionDispose?: () => void;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  get personalTeam() {
    return this.userTeams.find((team) => team.personal);
  }

  get sortedUserTeams() {
    return orderBy(this.userTeams, [(team) => team.personal, "title"], ["desc", "asc"]);
  }

  lazyEnable() {
    this.loadTeams();
    this.userDataReactionDispose?.();
    this.userDataReactionDispose = reaction(
      () => this.rootStore.auth.userData,
      () => {
        this.loadTeams();
      },
    );
  }

  async fetchTeams(signal?: AbortSignal) {
    runInAction(() => {
      this.fetchingProcess = true;
    });
    const userTeamsResult = await userTeams({ signal });
    if (userTeamsResult.teams) {
      this.setTeams(userTeamsResult.teams);
      this.setCurrentTeam();
    }
    runInAction(() => {
      this.fetchingProcess = false;
    });
  }

  loadTeams() {
    if (this.rootStore.auth.userData) {
      this.loadTeamsDispose?.();
      this.loadTeamsDispose = disposeApi(async (signal) => {
        await this.fetchTeams(signal);
      });
    } else {
      this.reset();
    }
  }

  setCurrentTeam(newCurrentTeam?: TeamModel) {
    const teamIdFromLocalStorage = localStorage.getItem(CURRENT_TEAM_STORAGE_KEY);
    const previousTeam = this.currentTeam;
    this.currentTeam = undefined;

    if (newCurrentTeam) {
      localStorage.setItem(CURRENT_TEAM_STORAGE_KEY, newCurrentTeam.uid);
      this.currentTeam = newCurrentTeam;
    }
    if (!this.currentTeam && teamIdFromLocalStorage) {
      this.currentTeam = this.userTeams.find(({ uid }) => uid === teamIdFromLocalStorage);
    }
    if (!this.currentTeam) {
      this.currentTeam = this.userTeams.find((team) => team.personal);
    }
    if (!this.currentTeam) {
      this.currentTeam = this.userTeams[0];
    }

    if (previousTeam && previousTeam !== this.currentTeam) {
      previousTeam.unsubscribe();
    }
    this.currentTeam?.subscribe();
  }

  setTeams(teams: TeamShort[]) {
    this.userTeams = teams.map((team) => new TeamModel(this.rootStore, team));
  }

  reset() {
    this.loadTeamsDispose?.();
    this.currentTeam?.unsubscribe();
  }

  dispose() {
    this.reset();
    this.userDataReactionDispose?.();
  }
}
